import { useContext, useEffect, useRef, useMemo, useCallback } from "react"
import { Field, FastField, useFormikContext } from "formik"
import _ from "lodash"
import { FormContext } from "../../context/formContext"
import { Editor } from "primereact/editor"
import { ResizableBox } from 'react-resizable'


// Helpers
import checkIfRequired from "./helpers/checkIfRequired"
import fieldVisibility from "../../helpers/fieldVisibility"
import sanitizeHtml from "sanitize-html"

import styles from "../../styles/Form.module.css"
import editorStyles from "../../styles/TypeTextareaRich.module.css"
import 'react-resizable/css/styles.css'

const renderHeader = () => {
  return (
    <div className="ql-toolbar">
      <span className="ql-formats">
        <button className="ql-bold" aria-label="Bold"></button>
        <button className="ql-italic" aria-label="Italic"></button>
        <button className="ql-underline" aria-label="Underline"></button>
      </span>
      <span className="ql-formats">
        <button
          className="ql-list"
          aria-label="Ordered List"
          value="ordered"
        ></button>
        <button
          className="ql-list"
          aria-label="Unordered List"
          value="bullet"
        ></button>
      </span>
    </div>
  )
}

const header = renderHeader()

const sanitizeHtmlOptions = {
  allowedTags: ["strong", "p", "li", "ul", "ol", "em", "u", "br"],
  parseStyleAttributes: false,
}

function DebouncedRichTextField({ formikField, sanitize, ...props }) {
  const { setFieldValue, setFieldTouched } = useFormikContext()
  const { name, value } = formikField

  // Keep track of the current value with a ref to avoid stale closures in the debounced function.
  const valueRef = useRef(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])

  // Memoize the debounced function to avoid re-creating it on every render.
  const debouncedSetFieldValue = useMemo(
    () =>
      _.debounce((fieldName, fieldValue) => {
        const clean = sanitize
          ? sanitizeHtml(fieldValue, sanitizeHtmlOptions)
          : fieldValue
        // Only update if the value has actually changed to avoid unnecessary re-renders.
        if (valueRef.current !== clean) {
          setFieldValue(fieldName, clean)
        }
      }, 500), // 500ms debounce delay
    [setFieldValue, sanitize]
  )

  // This effect ensures that any pending debounced updates are flushed
  // when the component unmounts. This is crucial for saving the last
  // typed value when the user navigates away quickly.
  useEffect(() => {
    return () => {
      debouncedSetFieldValue.flush()
    }
  }, [debouncedSetFieldValue])

  const handleTextChange = useCallback(
    (e) => {
      // Call the debounced function on every text change.
      debouncedSetFieldValue(name, e.htmlValue)
    },
    [name, debouncedSetFieldValue]
  )

  const handleBlur = useCallback(() => {
    // Flush any pending changes immediately on blur.
    debouncedSetFieldValue.flush()
    // Manually mark the field as touched for Formik's validation.
    setFieldTouched(name, true)
  }, [name, setFieldTouched, debouncedSetFieldValue])

  return (
    <div className={editorStyles.richTextEditor}>
      <ResizableBox
        height={150} // Initial height
        minConstraints={[0, 150]} // Set a minimum height to prevent collapsing
        maxConstraints={[Infinity, 500]} // Set a maximum height to prevent excessive growth
        axis="y" // Only allow vertical resizing
        style={{ paddingBottom: 3, paddingRight: 3 }} // Add some spacing below the editor
      >
        <Editor
          name={name}
          value={value || ""}
          onTextChange={handleTextChange}
          onBlur={handleBlur}
          {...props}
        />
      </ResizableBox>
    </div>
  )
}

function RegularFieldOutput(props) {
  const { field, isRequired, block, index } = props
  const form = useContext(FormContext)
  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: fieldVisibility(field.field_visibility),
      }}
    >
      <label className={styles.label} style={{ marginBottom: 5 }}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, form, meta }) => {
            if (isDisabled) {
              return (
                <div dangerouslySetInnerHTML={{ __html: formikField.value }} />
              )
            }
            return (
              <>
                <DebouncedRichTextField
                  formikField={formikField}
                  sanitize={true}
                  headerTemplate={header}
                />
                {meta.touched && meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </Field>
      </div>
    </div>
  )
}

function MultiFieldOutput(props) {
  const { field, index, mfFieldName, mfIndex } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${field.field_name}`
  const isRequired = checkIfRequired(props.field.field_validation.required[0])
  const isReadOnly = Boolean(field.read_only)

  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <FastField name={fieldSlug}>
          {({ field: fastField, form, meta }) => {
            if (isReadOnly) {
              return <div dangerouslySetInnerHTML={{ __html: fastField.value }} />
            }

            return (
              <>
                <DebouncedRichTextField
                  formikField={fastField}
                  sanitize={true}
                  headerTemplate={header}
                />
                {meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </FastField>
      </div>
    </div >
  )
}

export default function TypeTextareaRich(props) {
  const form = useContext(FormContext)

  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(props.field)

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(props.field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  switch (props.type) {
    case "regular-field":
      const regFieldProps = {
        field: props.field,
        isRequired: checkIfRequired(props.field.field_validation.required[0]),
        block: props.block,
        index: props.index,
      }
      return <RegularFieldOutput {...regFieldProps} />
    case "multi-field":
      const multiFieldProps = {
        field: props.field,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
      }
      return <MultiFieldOutput {...multiFieldProps} />
    default:
      return null
  }
}
